import { ApiClient } from '../client'
import { 
  Organisation, 
  CreateOrganisationRequest,
  UpdateTargetPoolRequest,
  ApiResponse 
} from '../types'

export class OrganisationApi {
  constructor(private client: ApiClient) {}

  async list(params: { page?: number; limit?: number } = {}): Promise<Organisation[]> {
    // Backend might return either paginated or direct array
    const response = await this.client.get<any>('/v1/organisations', params)
    
    // Handle both paginated and direct array responses
    if (Array.isArray(response)) {
      return response
    } else if (response?.data && Array.isArray(response.data)) {
      return response.data
    } else if (response?.items && Array.isArray(response.items)) {
      return response.items
    }
    
    return []
  }

  async get(orgId: string): Promise<Organisation> {
    const response = await this.client.get<any>(`/v1/organisations/${orgId}`)
    return response?.data || response
  }

  async create(data: CreateOrganisationRequest): Promise<ApiResponse<Organisation>> {
    return this.client.post<ApiResponse<Organisation>>('/v1/organisations', data)
  }

  async update(orgId: string, data: { organisation_display_name?: string; settings?: any }): Promise<ApiResponse<Organisation>> {
    return this.client.patch<ApiResponse<Organisation>>(`/v1/organisations/${orgId}`, data)
  }

  async delete(orgId: string): Promise<void> {
    return this.client.delete<void>(`/v1/organisations/${orgId}`)
  }

  // Target management
  async getTargets(orgId: string): Promise<any> {
    const response = await this.client.get<any>(`/v1/organisations/${orgId}/targets`)
    return response?.data || response
  }

  async updateTargets(
    orgId: string, 
    data: UpdateTargetPoolRequest
  ): Promise<{ total_count: number; valid_emails: number; invalid_emails: number }> {
    const response = await this.client.put<any>(`/v1/organisations/${orgId}/targets`, data)
    return response?.data || response
  }

  async addTargets(orgId: string, emails: string[]): Promise<ApiResponse<{ added: number; rejected: number }>> {
    return this.client.post<ApiResponse<any>>(`/v1/organisations/${orgId}/targets/add`, { emails })
  }

  async removeTargets(orgId: string, emails: string[]): Promise<ApiResponse<{ removed: number }>> {
    return this.client.post<ApiResponse<any>>(`/v1/organisations/${orgId}/targets/remove`, { emails })
  }

  /**
   * Verify domain ownership via DNS TXT record
   * POST /v1/organisations/{id}/verify
   */
  async verifyDomain(orgId: string): Promise<{
    organisation_id: string
    domain: string
    verified_at?: string
    message: string
  }> {
    const response = await this.client.post<any>(`/v1/organisations/${orgId}/verify`, {})
    return response?.data || response
  }
}
